import { 
  UserAgent, 
  Registerer, 
  Inviter, 
  Invitation,
  SessionState,
  RegistererState,
  Session,
  UserAgentOptions,
  URI
} from 'sip.js';
import type { IncomingResponse } from 'sip.js/lib/core';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import {
  browserLifecycle,
  createDecorrelatedJitter,
  type DecorrelatedJitterBackoff,
  type LifecycleEvent,
} from '@easyflow/ui-frontend';
import type {
  KeepaliveWorkerInbound,
  KeepaliveWorkerOutbound,
} from './sipKeepaliveWorker';

export type CallState = 'idle' | 'registering' | 'registered' | 'unregistered' | 'calling' | 'ringing' | 'incoming' | 'connected' | 'on_hold' | 'error';

export interface SipPhoneConfig {
  server: string;
  extension: string;
  password: string;
  domain: string;
  displayName?: string;
}

export interface CallInfo {
  direction: 'inbound' | 'outbound';
  remoteNumber: string;
  remoteName?: string;
  startTime: Date;
  duration: number;
  isHeld: boolean;
  isMuted: boolean;
}

type SipPhoneEventCallback = (state: CallState, callInfo?: CallInfo) => void;

// Task #2569 — soglie liveness softphone (evidence-based watchdog + probe).
const WATCHDOG_INTERVAL_MS = 15000; // cadenza del watchdog
const STALE_THRESHOLD_MS = 45000;   // oltre questo dall'ultimo round-trip riuscito -> probe attivo
const PROBE_DEADLINE_MS = 8000;     // deadline sulla risposta al re-REGISTER di probe
const REGISTER_DEADLINE_MS = 10000; // Task #2708 — deadline sull'accept REALE della REGISTER iniziale
// Task #2989 — heartbeat keepalive su Web Worker. Intervallo con margine ampio
// sotto la soglia server (keepalive_timeout KAM-1): tiene viva la WSS anche a
// tab in secondo piano (i timer del Worker non sono throttlati come quelli di
// pagina). 12s << 30/60s server: anche perdendo qualche tick sotto throttling
// intensivo la connessione non resta mai idle abbastanza da essere chiusa.
const WORKER_KEEPALIVE_INTERVAL_MS = 12000;
// Keepalive nativo SIP.js (timer di PAGINA, throttlato in background): abbassato
// da 30s a 20s per dare margine in primo piano. Il keep-alive PRIMARIO in
// background resta il Worker sopra.
const NATIVE_KEEPALIVE_INTERVAL_S = 20;
// 429 Too Many Requests dal SBC (pipelimit/pike KAM-1): un reject di rate-limit
// NON e` un transport morto ne` un errore fatale. Va rispettato l'header
// `Retry-After`, altrimenti il reconnect ravvicinato (backoff base 1s + teardown
// WSS + nuova REGISTER) perpetua il flood e il client si auto-infligge il ban.
const RATE_LIMIT_FALLBACK_MS = 30000; // 429 senza Retry-After -> attesa prudenziale
const RATE_LIMIT_MAX_MS = 120000;     // cap difensivo sul Retry-After annunciato
// Task #3158 — un reject del server sul REGISTER (403 da ACL/rollout SBC, 404
// da provisioning gate, 5xx transitori) NON e` mai definitivo: il softphone
// ritenta per sempre con un backoff dedicato piu` lento (30s -> 5min) per non
// martellare il SBC, e la UI mostra "non registrato" finche` non recupera.
// Sostituisce il vecchio `_authFatal` che dopo un 403 fermava ogni recupero
// fino al reload manuale della pagina (incident cutover EXT_ACL).
const SERVER_REJECT_BASE_MS = 30000;  // primo retry dopo un reject
const SERVER_REJECT_MAX_MS = 300000;  // cap del backoff sui reject persistenti

export class SipPhoneService {
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private currentSession: Session | null = null;
  private config: SipPhoneConfig | null = null;

  public getCurrentExtension(): string | null {
    return this.config?.extension || null;
  }

  /**
   * Task #3158 — status code dell'ultimo reject del server sul REGISTER
   * (403/404/5xx...), null se registrati o mai rifiutati. Espone alla UI il
   * motivo dello stato "non registrato" mentre il retry con backoff e` in corso.
   */
  public get registerRejectCode(): number | null {
    return this.serverRejectCode;
  }
  private eventCallback: SipPhoneEventCallback | null = null;
  private callStartTime: Date | null = null;
  private durationInterval: NodeJS.Timeout | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private isHeld: boolean = false;
  private isMuted: boolean = false;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isReconnecting: boolean = false;
  private isReinviteInProgress: boolean = false;
  // Task #3158 — ultimo status code di reject del server sul REGISTER (403/404/
  // 5xx). Non e` mai "fatale": instrada il retry sul backoff lento dedicato
  // (SERVER_REJECT_BASE_MS -> SERVER_REJECT_MAX_MS) e la UI su 'unregistered'.
  private serverRejectCode: number | null = null;
  private readonly rejectBackoff: DecorrelatedJitterBackoff = createDecorrelatedJitter({ baseMs: SERVER_REJECT_BASE_MS, maxMs: SERVER_REJECT_MAX_MS });
  // 429 dal SBC: timestamp (epoch ms) prima del quale NON si deve ri-REGISTER.
  private rateLimitedUntil: number = 0;
  private reconnectInFlight: Promise<boolean> | null = null;
  private connectionEpoch: number = 0;
  // Task #2569 — liveness evidence-based + backoff infinito con jitter de-correlato.
  private readonly backoff: DecorrelatedJitterBackoff = createDecorrelatedJitter({ baseMs: 1000, maxMs: 30000 });
  private watchdogInterval: NodeJS.Timeout | null = null;
  private lastRoundTripAt: number = 0;
  private probeInFlight: Promise<boolean> | null = null;
  // Task #2989 — il reconnect non e` piu` sospeso a tab nascosta (requisito
  // liveness background); viene differito SOLO quando il browser e` davvero
  // offline (navigator.onLine === false), per non ciclare a vuoto.
  private reconnectDeferredWhileOffline: boolean = false;
  // Task #2989 — Worker dedicato per l'heartbeat keepalive (timer non throttlati).
  private keepaliveWorker: Worker | null = null;
  private readonly lifecycleUnsubscribe: () => void;

  constructor() {
    this.remoteAudio = new Audio();
    this.remoteAudio.autoplay = true;
    // Unico observer lifecycle condiviso: al ritorno-in-primo-piano / online
    // ri-validiamo subito la liveness e riprendiamo un reconnect sospeso.
    this.lifecycleUnsubscribe = browserLifecycle.subscribe(this.handleLifecycle);
    // Task #2989 — chiusura pulita della tab/navigazione: un-REGISTER best-effort
    // cosi` il SBC ci marca offline subito (vedi handlePageHide).
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handlePageHide);
    }
  }

  private isDebugMode(): boolean {
    return localStorage.getItem('sip_debug_mode') === 'true';
  }

  private isAutoAnswer(): boolean {
    return localStorage.getItem('sip_auto_answer') === 'true';
  }

  private log(message: string, ...args: unknown[]): void {
    if (this.isDebugMode()) {
      console.log(`[SipPhone DEBUG] ${message}`, ...args);
    }
  }

  public setEventCallback(callback: SipPhoneEventCallback) {
    this.eventCallback = callback;
  }

  private emit(state: CallState, callInfo?: CallInfo) {
    if (this.eventCallback) {
      this.eventCallback(state, callInfo);
    }
  }

  private getCallInfo(): CallInfo | undefined {
    if (!this.currentSession) return undefined;
    
    const remoteIdentity = this.currentSession.remoteIdentity;
    const direction = this.currentSession instanceof Inviter ? 'outbound' : 'inbound';
    
    return {
      direction,
      remoteNumber: remoteIdentity?.uri?.user || 'Unknown',
      remoteName: remoteIdentity?.displayName || undefined,
      startTime: this.callStartTime || new Date(),
      duration: this.callStartTime ? Math.floor((Date.now() - this.callStartTime.getTime()) / 1000) : 0,
      isHeld: this.isHeld,
      isMuted: this.isMuted
    };
  }

  private startDurationTimer() {
    this.callStartTime = new Date();
    this.durationInterval = setInterval(() => {
      if (this.currentSession && this.currentSession.state === SessionState.Established) {
        this.emit('connected', this.getCallInfo());
      }
    }, 1000);
  }

  private stopDurationTimer() {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
    this.callStartTime = null;
  }

  private setupSessionHandlers(session: Session) {
    session.stateChange.addListener((state: SessionState) => {
      console.log(`[SipPhone] Session state changed: ${state} (session: ${session.id}, current: ${this.currentSession?.id || 'none'})`);
      
      switch (state) {
        case SessionState.Establishing:
          if (session instanceof Inviter) {
            this.emit('calling', this.getCallInfo());
          }
          break;
        case SessionState.Established:
          // Only setup media if this is our current session
          if (this.currentSession === session) {
            this.setupRemoteMedia(session);
            this.startDurationTimer();
            this.emit('connected', this.getCallInfo());
          }
          break;
        case SessionState.Terminated:
          // CRITICAL: Only reset state if this is the CURRENT session
          // Ignore termination of forked/old sessions to prevent UI reset
          if (this.currentSession === session) {
            console.log('[SipPhone] Current session terminated, resetting state');
            this.stopDurationTimer();
            this.currentSession = null;
            this.isHeld = false;
            this.isMuted = false;
            this.emit('registered');
          } else {
            console.log('[SipPhone] Ignoring termination of non-current session');
          }
          break;
      }
    });
  }

  private setupRemoteMedia(session: Session) {
    const sessionDescriptionHandler = session.sessionDescriptionHandler as SessionDescriptionHandler;
    if (!sessionDescriptionHandler) return;

    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) return;

    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        const remoteStream = new MediaStream([receiver.track]);
        if (this.remoteAudio) {
          this.remoteAudio.srcObject = remoteStream;
          this.remoteAudio.play().catch(e => console.error('[SipPhone] Audio play error:', e));
        }
      }
    });
  }

  /**
   * Connessione iniziale instradata nel funnel single-flight (performReconnect).
   * Root cause storica "non si autentica dopo il reload": connect() creava la UA
   * FUORI dal guard `reconnectInFlight`, cosi` il liveness check lanciato dal
   * lifecycle "foreground" (che scatta proprio al load pagina, con
   * lastRoundTripAt=0 -> sempre stale) faceva performReconnect() e stoppava la
   * UA a meta` handshake: la REGISTER partiva su transport morto -> 503 interno
   * -> backoff lento; il doppio burst di REGISTER faceva pure scattare il
   * pipelimit del SBC (429). Nel funnel il guard chiude la race alla radice.
   */
  public async connect(config: SipPhoneConfig): Promise<boolean> {
    this.config = config;
    const ok = await this.performReconnect();
    // Primo connect fallito con config ancora attiva (nessun disconnect nel
    // frattempo): arma il retry. Senza questo, un errore di transport al primo
    // giro lasciava il telefono morto fino al reload (emit 'error' e basta).
    if (!ok && this.config === config) {
      this.scheduleReconnect();
    }
    return ok;
  }

  /**
   * Build and register a fresh UserAgent for the given config and return the
   * created instance (or null on failure). Deliberately does NOT touch
   * `this.config`: ownership of the config lifecycle stays with the caller
   * (connect / disconnect), so a reconnect that races a disconnect can never
   * re-arm a config the user just cleared.
   */
  private async establishConnection(config: SipPhoneConfig): Promise<UserAgent | null> {
    let ua: UserAgent | null = null;
    try {
      this.emit('registering');

      const uri = UserAgent.makeURI(`sip:${config.extension}@${config.domain}`);
      if (!uri) {
        throw new Error('Failed to create URI');
      }

      const transportOptions = {
        server: config.server,
        traceSip: false,
        // Critical: keepAliveInterval prevents WebSocket 1006 disconnects.
        // Task #2989 — abbassato da 30s a 20s per dare margine in primo piano;
        // in background il keep-alive primario e` il Web Worker (non throttlato),
        // questo timer di pagina e` throttlato a tab nascosta.
        keepAliveInterval: NATIVE_KEEPALIVE_INTERVAL_S,
        keepAliveDebounce: 10      // Wait 10 sec before retry after failure
      };

      const userAgentOptions: UserAgentOptions = {
        uri,
        transportOptions,
        authorizationUsername: config.extension,
        authorizationPassword: config.password,
        displayName: config.displayName || config.extension,
        sessionDescriptionHandlerFactoryOptions: {
          constraints: {
            audio: true,
            video: false
          },
          peerConnectionConfiguration: {
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
              { urls: 'stun:stun2.l.google.com:19302' }
            ],
            iceCandidatePoolSize: 5,
            bundlePolicy: 'max-compat',
            rtcpMuxPolicy: 'negotiate'
          },
          iceGatheringTimeout: 1000
        },
        delegate: {
          onInvite: (invitation: Invitation) => {
            this.handleIncomingCall(invitation);
          }
        }
      };

      ua = new UserAgent(userAgentOptions);
      this.userAgent = ua;

      await ua.start();
      console.log('[SipPhone] UserAgent started');

      // Wait for transport to be connected before registering
      const transport = ua.transport;
      if (transport && transport.state !== 'Connected') {
        console.log('[SipPhone] Waiting for transport to connect...');
        await new Promise<void>((resolve, reject) => {
          let timeout: NodeJS.Timeout;
          const cleanup = () => {
            clearTimeout(timeout);
            transport.stateChange.removeListener(checkState);
          };
          const checkState = () => {
            if (transport.state === 'Connected') {
              cleanup();
              resolve();
            }
          };
          timeout = setTimeout(() => {
            // Always remove the listener on timeout too, otherwise it leaks
            // onto the (now stale) transport until GC.
            cleanup();
            reject(new Error('Transport connection timeout'));
          }, 10000);

          transport.stateChange.addListener(checkState);
          checkState(); // Check immediately in case already connected
        });
        console.log('[SipPhone] Transport connected');
      }
      
      // Small delay to ensure transport is fully ready
      await new Promise(resolve => setTimeout(resolve, 300));

      this.registerer = new Registerer(ua, {
        expires: 300,
        refreshFrequency: 70
      });
      
      this.registerer.stateChange.addListener((state: RegistererState) => {
        console.log(`[SipPhone] Registerer state: ${state}`);
        switch (state) {
          case RegistererState.Registered:
            // Evidence di un round-trip vivo: il server ha accettato il REGISTER.
            this.lastRoundTripAt = Date.now();
            // Task #3158 — un accept (anche di refresh, senza delegate) chiude
            // la finestra di reject: azzera il backoff lento dedicato.
            this.serverRejectCode = null;
            this.rejectBackoff.reset();
            this.emit('registered');
            break;
          case RegistererState.Unregistered: {
            // Task #2989 — un 503 transitorio (es. REGISTER di refresh su
            // transport morto in background) NON deve azzerare la UI: il
            // SipDial resta visibile e il recupero (watchdog/backoff) ripristina
            // la registrazione. Disaccoppiamo la transizione Unregistered dalla
            // pulizia UI (emit 'idle'), che resta SOLO per i casi definitivi.
            const recovering =
              this.isReconnecting ||
              !!this.reconnectInFlight ||
              !!this.reconnectTimeout ||
              this.reconnectDeferredWhileOffline;
            // Teardown esplicito (config azzerata da disconnect): fallimento
            // definitivo -> la UI puo` azzerarsi.
            if (!this.config) {
              this.emit('idle');
              break;
            }
            // Task #3158 — la UI deve mostrare chiaramente "non registrato"
            // finche` il recupero non chiude il giro (mai durante una chiamata
            // viva: li` lo stato UI appartiene alla sessione).
            if (!this.currentSession) {
              this.emit('unregistered');
            }
            // Recupero gia` in volo/pendente: niente doppio schedule.
            if (recovering) {
              break;
            }
            // Unregistered inatteso con config ancora attiva: NON azzeriamo la
            // UI, avviamo il recupero (single-flight). Se il transport e` morto
            // lo schedule/probe lo ripristina senza logout/login.
            console.warn('[SipPhone] Unregistered inatteso, avvio recupero senza azzerare la UI');
            this.scheduleReconnect();
            break;
          }
        }
      });

      // Task #2708 — L'esito della REGISTER iniziale e` AUTOREVOLE: attendiamo
      // l'accept REALE (onAccept) con una deadline esplicita, NON il semplice
      // resolve di register() (che scatta quando la richiesta e` SPEDITA, non
      // ACCETTATA). Su transport mezzo-morto il resolve farebbe credere a un
      // successo fantasma mentre il Registerer resta Unregistered.
      const accepted = await this.awaitInitialRegister(this.registerer, REGISTER_DEADLINE_MS);
      if (!accepted) {
        // Nessun successo fantasma: niente log "Registered successfully", niente
        // lastRoundTripAt/backoff.reset() fasulli. La connessione NON e` valida
        // -> il catch sotto fa teardown del UA mezzo-morto e torna null, cosi`
        // il chiamante (connect / performReconnect) instrada al recupero
        // esistente (reconnect single-flight) o emette 'error'.
        throw new Error('Initial REGISTER not accepted (rejected/timeout on dead transport)');
      }
      console.log('[SipPhone] Registered successfully');

      // Setup transport disconnect handler for auto-reconnect. Solo qui, sul
      // ramo di accept confermato, seminiamo i marker di liveness e avviamo il
      // watchdog: lo stato "registrato" non e` mai fantasma.
      this.setupTransportHandlers();
      this.backoff.reset();
      this.lastRoundTripAt = Date.now();
      this.startWatchdog();
      // Task #2989 — heartbeat keepalive su Worker: tiene viva la WSS anche a
      // tab in secondo piano. Idempotente (riuso a reconnect).
      this.startKeepaliveWorker();

      return ua;
    } catch (error) {
      console.error('[SipPhone] Connection error:', error);
      // Transactional cleanup: never leave a half-started UserAgent behind,
      // otherwise the next reconnect inherits a stale/partial instance. Operate
      // on the instance THIS attempt created (ownership-safe): a concurrent
      // connect/reconnect may already have installed a newer UserAgent that we
      // must not stop or null out.
      if (ua) {
        try {
          await ua.stop();
        } catch (stopErr) {
          console.warn('[SipPhone] Cleanup after failed connect: stop failed:', stopErr);
        }
      }
      if (this.userAgent === ua) {
        this.userAgent = null;
        this.registerer = null;
      }
      // Task #3158 — un reject del server (403/404/5xx) NON e` un errore
      // definitivo: la UI mostra "non registrato" e il retry con backoff lento
      // resta armato (il chiamante performReconnect/connect instrada allo
      // schedule; qui copriamo anche il primo connect diretto).
      if (this.serverRejectCode !== null && this.config) {
        this.emit('unregistered');
        this.scheduleReconnect();
      } else {
        this.emit('error');
      }
      return null;
    }
  }

  private setupTransportHandlers(): void {
    if (!this.userAgent?.transport) return;
    
    const transport = this.userAgent.transport;
    
    transport.stateChange.addListener((state) => {
      console.log(`[SipPhone] Transport state: ${state}`);
      
      // Mai forzare reconnect mentre e` in corso una chiamata: performReconnect
      // farebbe userAgent.stop() uccidendo la sessione viva. Al termine della
      // chiamata il watchdog ricontrolla e recupera.
      if (state === 'Disconnected' && !this.isReconnecting && this.config && !this.currentSession) {
        console.warn('[SipPhone] Transport disconnected, scheduling reconnect...');
        this.scheduleReconnect();
      }
    });
  }

  /**
   * Single-flight reconnect: stop the stale UserAgent and rebuild the
   * connection. Both the event-driven backoff (scheduleReconnect) and the
   * user-initiated self-heal (ensureTransportReady) funnel through here, so a
   * reconnect already in progress is never duplicated (no double UserAgent,
   * no flapping, no duplicated transport listeners).
   */
  private performReconnect(): Promise<boolean> {
    if (this.reconnectInFlight) return this.reconnectInFlight;
    const config = this.config;
    if (!config) return Promise.resolve(false);
    // Snapshot the connection generation: an explicit disconnect() bumps it, so
    // a reconnect that completes AFTER a teardown is recognised as superseded
    // and torn down instead of resurrecting a phone the user shut off.
    const epoch = this.connectionEpoch;

    const run = (async (): Promise<boolean> => {
      this.isReconnecting = true;
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch (e) {
          console.warn('[SipPhone] performReconnect: error stopping old UserAgent:', e);
        }
      }
      this.userAgent = null;
      this.registerer = null;
      // establishConnection waits for transport "Connected", awaits register()
      // and returns the exact UserAgent it created, so this reconnect owns that
      // instance and never re-arms this.config.
      const createdUa = await this.establishConnection(config);

      // If a disconnect happened while we were reconnecting, abandon this result
      // so the explicit teardown always wins (no ghost registration): tear down
      // ONLY the instance we created, never a newer one installed meanwhile.
      if (epoch !== this.connectionEpoch) {
        if (createdUa) {
          try {
            await createdUa.stop();
          } catch (e) {
            console.warn('[SipPhone] performReconnect: superseded by disconnect, stopping stale UA:', e);
          }
          if (this.userAgent === createdUa) {
            this.userAgent = null;
            this.registerer = null;
          }
        }
        return false;
      }
      return createdUa !== null;
    })();

    this.reconnectInFlight = run;
    return run.finally(() => {
      this.reconnectInFlight = null;
      this.isReconnecting = false;
    });
  }

  /**
   * Task #2569 — Reconnect loop con backoff infinito + jitter de-correlato.
   * Non si arrende mai: anche i reject del server (403/404/5xx) ritentano per
   * sempre, sul backoff lento dedicato (Task #3158).
   * Sospeso quando la tab e` nascosta (deferred), ripreso al ritorno in primo
   * piano. Funnel unico verso performReconnect (single-flight + connectionEpoch).
   */
  private scheduleReconnect(): void {
    if (this.isReconnecting || this.reconnectInFlight || this.reconnectTimeout || !this.config) return;
    // Task #2989 — il reconnect NON e` piu` sospeso a tab nascosta (deve
    // ripristinare la registrazione anche in background). Resta differito SOLO
    // quando il browser e` davvero offline: niente reconnect "a vuoto" finche`
    // la rete non torna; ripreso dal lifecycle handler all'evento 'online'.
    if (!browserLifecycle.isOnline()) {
      this.reconnectDeferredWhileOffline = true;
      return;
    }

    // Rispetta il 429/Retry-After del SBC: se siamo rate-limited non riprovare
    // prima della scadenza annunciata, altrimenti alimentiamo il flood (pipelimit
    // KAM) e ci auto-infliggiamo il ban -> dead transport.
    // Task #3158 — dopo un reject del server (403/404/5xx) il retry usa il
    // backoff lento dedicato (30s -> 5min): ritenta per sempre senza martellare.
    const rejected = this.serverRejectCode !== null;
    const backoffDelay = rejected ? this.rejectBackoff.next() : this.backoff.next();
    const rateLimitWait = this.rateLimitedUntil - Date.now();
    const delay = rateLimitWait > backoffDelay ? rateLimitWait : backoffDelay;
    console.log(`[SipPhone] Reconnect scheduled in ${delay}ms${rateLimitWait > 0 ? ' (rate-limited 429/Retry-After)' : rejected ? ` (REGISTER rejected ${this.serverRejectCode}, backoff lento)` : ' (backoff infinito)'}`);

    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectTimeout = null;
      const success = await this.performReconnect();
      if (success) {
        console.log('[SipPhone] Reconnected successfully');
        this.backoff.reset();
        this.rejectBackoff.reset();
      } else {
        this.scheduleReconnect();
      }
    }, delay);
  }

  /**
   * Task #2569 — Watchdog liveness basato su evidenza. Periodicamente (e al
   * risveglio via lifecycle) verifica il tempo dall'ultimo round-trip riuscito:
   * su transport non connesso schedula reconnect, su transport "Connected" ma
   * stantio lancia un probe attivo con deadline che smaschera l'half-open. Mai
   * durante una chiamata attiva (un reconnect farebbe userAgent.stop()
   * uccidendo la sessione viva) ne` a tab nascosta.
   */
  private startWatchdog(): void {
    if (this.watchdogInterval) return;
    this.watchdogInterval = setInterval(() => {
      void this.runLivenessCheck();
    }, WATCHDOG_INTERVAL_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }

  /**
   * Task #2989 — heartbeat keepalive su Web Worker. I timer di pagina vengono
   * throttlati a tab in secondo piano (anche fino a 1/min), facendo morire la
   * WSS per idle lato server e portando al 503 sul REGISTER di refresh. Un
   * Worker dedicato batte a intervallo regolare e sveglia il main thread, che
   * invia un keepalive (double-CRLF, la sequenza nativa SIP.js) sul transport.
   * Idempotente: riusato attraverso i reconnect.
   */
  private startKeepaliveWorker(): void {
    if (typeof Worker === 'undefined') return;
    if (this.keepaliveWorker) {
      this.keepaliveWorker.postMessage({
        type: 'start',
        intervalMs: WORKER_KEEPALIVE_INTERVAL_MS,
      } satisfies KeepaliveWorkerInbound);
      return;
    }
    try {
      const worker = new Worker(new URL('./sipKeepaliveWorker.ts', import.meta.url), {
        type: 'module',
      });
      worker.onmessage = (event: MessageEvent<KeepaliveWorkerOutbound>): void => {
        if (event.data.type === 'tick') {
          this.sendKeepalive();
        }
      };
      worker.postMessage({
        type: 'start',
        intervalMs: WORKER_KEEPALIVE_INTERVAL_MS,
      } satisfies KeepaliveWorkerInbound);
      this.keepaliveWorker = worker;
    } catch (error) {
      // Best-effort: senza Worker restano i keepalive nativi SIP.js + watchdog.
      console.warn('[SipPhone] keepalive worker non disponibile:', error);
    }
  }

  /**
   * Invia il keepalive sul transport SOLO se realmente connesso: mai usarlo come
   * punto di "scoperta" di un transport morto (quello e` compito del watchdog).
   */
  private sendKeepalive(): void {
    const transport = this.userAgent?.transport;
    if (!transport || transport.state !== 'Connected') return;
    void transport.send('\r\n\r\n').catch((error: unknown) => {
      console.warn('[SipPhone] keepalive send fallito:', error);
    });
  }

  private stopKeepaliveWorker(): void {
    if (!this.keepaliveWorker) return;
    this.keepaliveWorker.postMessage({ type: 'stop' } satisfies KeepaliveWorkerInbound);
    this.keepaliveWorker.terminate();
    this.keepaliveWorker = null;
  }

  /**
   * Task #2989 — chiusura tab/navigazione: un-REGISTER best-effort cosi` il SBC
   * ci marca offline senza attendere lo scadere della registrazione. `persisted`
   * = la pagina entra in bfcache (puo` tornare viva): in quel caso NON
   * disconnettiamo, fermiamo solo l'heartbeat (verra` ripreso al ritorno).
   */
  private readonly handlePageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      this.stopKeepaliveWorker();
      return;
    }
    this.stopKeepaliveWorker();
    if (this.registerer?.state === RegistererState.Registered) {
      void this.registerer.unregister();
    }
  };

  private async runLivenessCheck(): Promise<void> {
    if (!this.config) return;
    if (this.isReconnecting || this.reconnectInFlight || this.reconnectTimeout) return;
    // Mai disturbare una chiamata attiva / in setup / squillante.
    if (this.currentSession) return;
    // Task #2989 — NON piu` sospeso a tab nascosta: il probe deve smascherare
    // l'half-open anche in background. Se davvero offline non ha senso provare.
    if (!browserLifecycle.isOnline()) return;

    const transport = this.userAgent?.transport;
    if (!transport || transport.state !== 'Connected') {
      this.scheduleReconnect();
      return;
    }

    if (Date.now() - this.lastRoundTripAt < STALE_THRESHOLD_MS) return;

    const alive = await this.probeLiveness(PROBE_DEADLINE_MS);
    if (!alive && this.config && !this.isReconnecting && !this.reconnectInFlight) {
      // Se il probe ha incassato un 429 (rateLimitedUntil nel futuro) NON forzare
      // un reconnect immediato: alimenterebbe il flood pipelimit del SBC. Instrada
      // al reconnect schedulato, che rispetta la finestra 429/Retry-After.
      if (this.rateLimitedUntil - Date.now() > 0) {
        console.warn('[SipPhone] Liveness probe rate-limited (429), scheduling reconnect after Retry-After');
        this.scheduleReconnect();
        return;
      }
      // Task #3158 — probe rifiutato dal server (403/404/5xx): non e` un
      // half-open, instrada al retry schedulato col backoff lento dedicato.
      if (this.serverRejectCode !== null) {
        console.warn(`[SipPhone] Liveness probe rejected (${this.serverRejectCode}), scheduling slow-backoff reconnect`);
        this.scheduleReconnect();
        return;
      }
      console.warn('[SipPhone] Liveness probe failed (half-open), forcing reconnect');
      this.backoff.reset();
      void this.performReconnect().then((success) => {
        if (!success) this.scheduleReconnect();
      });
    }
  }

  /**
   * Task #2708 — Esito AUTOREVOLE della REGISTER iniziale. `register()` risolve
   * quando la richiesta e` SPEDITA, non quando e` ACCETTATA: su un transport
   * mezzo-morto la REGISTER fallisce con 503 interno ma il resolve farebbe
   * credere a un successo fantasma. Qui attendiamo l'accept REALE (onAccept) con
   * una deadline esplicita — stesso pattern del probe di liveness — e seminiamo
   * i marker di liveness SOLO su accept confermato. Niente accept entro la
   * deadline o reject -> false (nessun timestamp/log/backoff.reset() fasullo).
   */
  private awaitInitialRegister(registerer: Registerer, deadlineMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (accepted: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(accepted);
      };
      const timer = setTimeout(() => finish(false), deadlineMs);
      try {
        void registerer.register({
          requestDelegate: {
            onAccept: () => {
              // Evidenza di un round-trip vivo: il server ha accettato il REGISTER.
              this.lastRoundTripAt = Date.now();
              this.serverRejectCode = null;
              this.rejectBackoff.reset();
              this.rateLimitedUntil = 0;
              finish(true);
            },
            onReject: (response) => {
              this.classifyRegisterReject(response);
              finish(false);
            },
          },
        }).catch(() => finish(false));
      } catch {
        finish(false);
      }
    });
  }

  /**
   * Classifica il reject di un REGISTER.
   *  - 429 Too Many Requests -> rate-limit del SBC (pipelimit/pike KAM-1): NON e`
   *    un transport morto. Rispettiamo `Retry-After` posticipando il prossimo
   *    tentativo, cosi` il client non perpetua il flood con reconnect ravvicinati.
   *  - Task #3158: ogni altro reject (403 da ACL/rollout SBC, 404 da provisioning
   *    gate, 5xx transitori) NON e` mai definitivo: nell'incident del cutover
   *    EXT_ACL un 403 transitorio lasciava il softphone morto fino al reload.
   *    Marca lo stato "rifiutato dal server" -> retry infinito col backoff lento
   *    dedicato (30s -> 5min) + UI 'unregistered' finche` non recupera.
   */
  private classifyRegisterReject(response: IncomingResponse): void {
    const code = response.message.statusCode;
    if (code === 429) {
      const waitMs = this.parseRetryAfterMs(response);
      this.rateLimitedUntil = Date.now() + waitMs;
      console.warn(`[SipPhone] 429 Too Many Requests dal SBC: rispetto Retry-After, prossimo REGISTER tra ~${Math.round(waitMs / 1000)}s`);
      return;
    }
    // 503 generato INTERNAMENTE da SIP.js quando la REGISTER parte su un
    // transport morto ("Not connected"): NON e` un reject del server. Marcarlo
    // come server-reject instradava il recupero sul backoff lento (30s -> 5min)
    // lasciando il telefono "non registrato" per minuti dopo un semplice
    // reload. Un 503 VERO del server arriva su un WS connesso.
    if (code === 503 && this.userAgent?.transport?.state !== 'Connected') {
      console.warn('[SipPhone] 503 interno (transport morto), recupero via backoff transport veloce');
      return;
    }
    this.serverRejectCode = code ?? 0;
    console.warn(`[SipPhone] REGISTER rifiutato dal server (${code}): retry con backoff lento (30s -> 5min) fino al recupero`);
  }

  /**
   * Legge l'header `Retry-After` (secondi) da una risposta 429. Fallback a
   * RATE_LIMIT_FALLBACK_MS se assente/non valido; cap a RATE_LIMIT_MAX_MS.
   */
  private parseRetryAfterMs(response: IncomingResponse): number {
    const raw = response.message.getHeader('retry-after');
    const secs = raw ? parseInt(raw.trim(), 10) : NaN;
    if (Number.isFinite(secs) && secs > 0) {
      return Math.min(secs * 1000, RATE_LIMIT_MAX_MS);
    }
    return RATE_LIMIT_FALLBACK_MS;
  }

  /**
   * Probe attivo: re-REGISTER con deadline esplicita sulla risposta. Un
   * transport half-open (WS morto ma `state === 'Connected'`) non produce mai
   * onAccept/onReject entro la deadline -> ritorna false (evidenza di morte).
   * Single-flight: un probe gia` in corso viene riusato.
   */
  private probeLiveness(deadlineMs: number): Promise<boolean> {
    if (this.probeInFlight) return this.probeInFlight;
    const registerer = this.registerer;
    if (!registerer) return Promise.resolve(false);

    const probe = new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (alive: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(alive);
      };
      const timer = setTimeout(() => finish(false), deadlineMs);
      try {
        void registerer.register({
          requestDelegate: {
            onAccept: () => {
              this.lastRoundTripAt = Date.now();
              this.rateLimitedUntil = 0;
              finish(true);
            },
            onReject: (response) => {
              this.classifyRegisterReject(response);
              finish(false);
            },
          },
        }).catch(() => finish(false));
      } catch {
        finish(false);
      }
    });

    this.probeInFlight = probe;
    return probe.finally(() => {
      this.probeInFlight = null;
    });
  }

  /**
   * Lifecycle: al ritorno-in-primo-piano / online ri-validiamo subito la
   * liveness e riprendiamo un reconnect che era stato differito perche` offline.
   */
  private readonly handleLifecycle = (event: LifecycleEvent): void => {
    if (event.type === 'offline') return;
    if (!this.config) return;
    // Task #2989 — un reconnect differito si riprende al ritorno ONLINE (la
    // visibilita` non sospende piu` il recupero, vedi scheduleReconnect).
    if (this.reconnectDeferredWhileOffline) {
      this.reconnectDeferredWhileOffline = false;
      this.scheduleReconnect();
    }
    void this.runLivenessCheck();
  };

  /**
   * Ensure the SIP transport is actually connected AND registered before a
   * user-initiated action (e.g. placing a call). A stale Registerer can keep
   * reporting "Registered" for minutes after the underlying WebSocket died
   * (REGISTER refresh is ~70% of the 300s expiry), so relying on the registerer
   * state alone lets an INVITE go out on a dead transport -> internal 503.
   * On a mismatch this forces a clean, immediate reconnect and bypasses the
   * exponential-backoff exhaustion so the phone self-heals on the next dial.
   */
  public async ensureTransportReady(): Promise<boolean> {
    if (!this.config) return false;

    const transportConnected = this.userAgent?.transport?.state === 'Connected';
    const registered = this.registerer?.state === RegistererState.Registered;
    if (this.userAgent && transportConnected && registered) {
      return true;
    }

    console.warn('[SipPhone] ensureTransportReady: transport/registration stale, forcing reconnect');

    // Rispetta la finestra 429/Retry-After anche sull'azione utente: forzare un
    // REGISTER ora alimenterebbe il rate-limit del SBC (pipelimit KAM). Schedula
    // il recupero rispettoso e fallisci in modo esplicito -> il dial mostra
    // l'errore invece di unirsi al flood.
    if (this.rateLimitedUntil - Date.now() > 0) {
      console.warn('[SipPhone] ensureTransportReady: rate-limited (429), deferring reconnect until Retry-After');
      this.scheduleReconnect();
      return false;
    }

    // Azione esplicita dell'utente: azzera entrambi i backoff (anche quello
    // lento dei reject — il dial deve poter risvegliare subito il telefono),
    // poi funnel attraverso il reconnect single-flight (che annulla ogni
    // backoff pendente e ricostruisce la UA).
    this.backoff.reset();
    this.rejectBackoff.reset();
    this.serverRejectCode = null;
    return this.performReconnect();
  }

  public async disconnect(): Promise<void> {
    try {
      // Cancel any pending reconnect
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      this.isReconnecting = false;
      this.backoff.reset();
      this.rejectBackoff.reset();
      this.serverRejectCode = null;
      this.rateLimitedUntil = 0;
      this.reconnectDeferredWhileOffline = false;
      this.stopWatchdog();
      // Task #2989 — chiusura esplicita: ferma l'heartbeat keepalive.
      this.stopKeepaliveWorker();
      // Bump the generation so any reconnect already in flight recognises it has
      // been superseded and tears itself down instead of resurrecting the phone.
      // Do NOT null reconnectInFlight here: let the in-flight promise settle.
      this.connectionEpoch++;
      // Clear config BEFORE any await: scheduleReconnect()'s guard keys off
      // `!this.config`, so nulling it now blocks any reschedule that an in-flight
      // reconnect settling mid-teardown could otherwise trigger.
      this.config = null;
      
      if (this.currentSession) {
        await this.hangup();
      }
      if (this.registerer) {
        await this.registerer.unregister();
      }
      if (this.userAgent) {
        await this.userAgent.stop();
      }
      this.userAgent = null;
      this.registerer = null;
      this.emit('idle');
    } catch (error) {
      console.error('[SipPhone] Disconnect error:', error);
    }
  }

  private handleIncomingCall(invitation: Invitation) {
    const caller = invitation.remoteIdentity.uri.user;
    console.log('[SipPhone] Incoming call from:', caller, 'session:', invitation.id);
    this.log('Incoming call details:', {
      caller,
      sessionId: invitation.id,
      autoAnswer: this.isAutoAnswer(),
      currentSession: !!this.currentSession
    });
    
    // If already in a call or handling an incoming call, reject forked invitations
    if (this.currentSession) {
      const currentState = this.currentSession.state;
      if (currentState === SessionState.Established || 
          currentState === SessionState.Establishing ||
          currentState === SessionState.Initial) {
        console.log('[SipPhone] Already handling a session, ignoring forked invitation');
        this.log('Ignoring forked invitation, current session state:', currentState);
        // Still setup handlers to track this session but don't overwrite currentSession
        this.setupSessionHandlers(invitation);
        return;
      }
    }
    
    this.currentSession = invitation;
    this.setupSessionHandlers(invitation);
    this.emit('incoming', this.getCallInfo());
    
    // Auto-answer if enabled
    if (this.isAutoAnswer()) {
      console.log('[SipPhone] Auto-answer enabled, answering call automatically');
      this.log('Auto-answering call from:', caller);
      setTimeout(() => {
        this.answer().catch(err => {
          console.error('[SipPhone] Auto-answer failed:', err);
          this.log('Auto-answer error:', err);
        });
      }, 500); // Small delay to ensure UI is ready
    }
  }

  public async call(destination: string): Promise<boolean> {
    if (!this.config) {
      console.error('[SipPhone] Not connected (no config)');
      return false;
    }

    if (this.currentSession) {
      console.error('[SipPhone] Already in a call');
      return false;
    }

    // CRITICAL: the Registerer can report "Registered" for minutes after the
    // underlying WebSocket died, so the registerer check below is NOT enough.
    // Sending an INVITE on a dead transport yields an internal 503
    // ("Not connected" / "Failed to send initial outgoing request"). Verify the
    // transport is really up — and self-heal via reconnect — before dialing.
    if (!this.userAgent || this.userAgent.transport?.state !== 'Connected') {
      console.warn('[SipPhone] Transport not connected before call, forcing reconnect...');
      const ready = await this.ensureTransportReady();
      if (!ready || !this.userAgent) {
        console.error('[SipPhone] Cannot place call: transport unavailable');
        this.emit('error');
        return false;
      }
    }

    // Check if still registered - quick check, no delay
    if (this.registerer?.state !== RegistererState.Registered) {
      console.warn('[SipPhone] Not registered, attempting quick re-register...');
      try {
        await this.registerer?.register();
        // Minimal delay - registration is async and will complete during INVITE
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (e) {
        console.error('[SipPhone] Re-registration failed:', e);
        this.emit('error');
        return false;
      }
    }

    try {
      let sanitizedDest = destination.replace(/[\s\-\(\)]/g, '');
      if (sanitizedDest.startsWith('+39') && sanitizedDest.length > 5) {
        sanitizedDest = sanitizedDest.slice(3);
      } else if (sanitizedDest.startsWith('0039') && sanitizedDest.length > 6) {
        sanitizedDest = sanitizedDest.slice(4);
      }
      const targetUri = UserAgent.makeURI(`sip:${sanitizedDest}@${this.config.domain}`);
      if (!targetUri) {
        throw new Error('Failed to create target URI');
      }

      const inviter = new Inviter(this.userAgent, targetUri);
      this.currentSession = inviter;
      this.setupSessionHandlers(inviter);

      await inviter.invite();
      console.log(`[SipPhone] Calling ${sanitizedDest}`);
      return true;
    } catch (error) {
      console.error('[SipPhone] Call error:', error);
      this.currentSession = null;
      this.emit('error');
      return false;
    }
  }

  public async answer(): Promise<boolean> {
    if (!this.currentSession || !(this.currentSession instanceof Invitation)) {
      console.error('[SipPhone] No incoming call to answer');
      return false;
    }

    try {
      await (this.currentSession).accept();
      console.log('[SipPhone] Call answered');
      return true;
    } catch (error) {
      console.error('[SipPhone] Answer error:', error);
      return false;
    }
  }

  public async hangup(): Promise<boolean> {
    if (!this.currentSession) {
      console.error('[SipPhone] No active call');
      return false;
    }

    const session = this.currentSession;
    
    try {
      switch (session.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (session instanceof Inviter) {
            await session.cancel();
          } else if (session instanceof Invitation) {
            await (session).reject();
          }
          break;
        case SessionState.Established:
          await session.bye();
          break;
        case SessionState.Terminating:
        case SessionState.Terminated:
          console.log('[SipPhone] Session already terminating/terminated');
          break;
      }
      console.log('[SipPhone] Call ended');
      return true;
    } catch (error) {
      console.error('[SipPhone] Hangup error:', error);
      this.forceCleanup();
      return false;
    } finally {
      setTimeout(() => {
        if (this.currentSession === session && session.state !== SessionState.Terminated) {
          console.warn('[SipPhone] Force cleanup after timeout');
          this.forceCleanup();
        }
      }, 3000);
    }
  }

  private forceCleanup(): void {
    this.stopDurationTimer();
    this.currentSession = null;
    this.isHeld = false;
    this.isMuted = false;
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
    }
    this.emit('registered');
  }

  /**
   * Public method to force reset state when call is stuck/ghost
   * Use when UI shows a call but FreeSWITCH has no active calls
   */
  public forceReset(): void {
    console.warn('[SipPhone] Force reset called - clearing stuck call state');
    this.forceCleanup();
  }

  public async hold(): Promise<boolean> {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      return false;
    }

    // Prevent concurrent reinvite operations
    if (this.isReinviteInProgress) {
      console.warn('[SipPhone] Hold blocked - reinvite already in progress');
      return false;
    }

    try {
      this.isReinviteInProgress = true;
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler as SessionDescriptionHandler;
      if (sessionDescriptionHandler) {
        await this.currentSession.invite({
          requestDelegate: {
            onAccept: () => {
              this.isHeld = true;
              this.isReinviteInProgress = false;
              this.emit('on_hold', this.getCallInfo());
            },
            onReject: () => {
              this.isReinviteInProgress = false;
            }
          },
          sessionDescriptionHandlerModifiers: [
            (description) => {
              description.sdp = description.sdp?.replace(/a=sendrecv/g, 'a=sendonly');
              return Promise.resolve(description);
            }
          ]
        });
      }
      return true;
    } catch (error) {
      this.isReinviteInProgress = false;
      console.error('[SipPhone] Hold error:', error);
      return false;
    }
  }

  public async unhold(): Promise<boolean> {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      return false;
    }

    // Prevent concurrent reinvite operations
    if (this.isReinviteInProgress) {
      console.warn('[SipPhone] Unhold blocked - reinvite already in progress');
      return false;
    }

    try {
      this.isReinviteInProgress = true;
      await this.currentSession.invite({
        requestDelegate: {
          onAccept: () => {
            this.isHeld = false;
            this.isReinviteInProgress = false;
            this.emit('connected', this.getCallInfo());
          },
          onReject: () => {
            this.isReinviteInProgress = false;
          }
        },
        sessionDescriptionHandlerModifiers: [
          (description) => {
            description.sdp = description.sdp?.replace(/a=sendonly/g, 'a=sendrecv');
            return Promise.resolve(description);
          }
        ]
      });
      return true;
    } catch (error) {
      this.isReinviteInProgress = false;
      console.error('[SipPhone] Unhold error:', error);
      return false;
    }
  }

  public mute(): boolean {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      return false;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler as SessionDescriptionHandler;
      const peerConnection = sessionDescriptionHandler?.peerConnection;
      
      if (peerConnection) {
        peerConnection.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'audio') {
            sender.track.enabled = false;
          }
        });
        this.isMuted = true;
        this.emit('connected', this.getCallInfo());
        return true;
      }
      return false;
    } catch (error) {
      console.error('[SipPhone] Mute error:', error);
      return false;
    }
  }

  public unmute(): boolean {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      return false;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler as SessionDescriptionHandler;
      const peerConnection = sessionDescriptionHandler?.peerConnection;
      
      if (peerConnection) {
        peerConnection.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'audio') {
            sender.track.enabled = true;
          }
        });
        this.isMuted = false;
        this.emit('connected', this.getCallInfo());
        return true;
      }
      return false;
    } catch (error) {
      console.error('[SipPhone] Unmute error:', error);
      return false;
    }
  }

  public async transfer(destination: string): Promise<boolean> {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established || !this.config) {
      return false;
    }

    try {
      let sanitizedDest = destination.replace(/[\s\-\(\)]/g, '');
      if (sanitizedDest.startsWith('+39') && sanitizedDest.length > 5) {
        sanitizedDest = sanitizedDest.slice(3);
      } else if (sanitizedDest.startsWith('0039') && sanitizedDest.length > 6) {
        sanitizedDest = sanitizedDest.slice(4);
      }
      const targetUri = UserAgent.makeURI(`sip:${sanitizedDest}@${this.config.domain}`);
      if (!targetUri) {
        throw new Error('Invalid transfer destination');
      }

      await this.currentSession.refer(targetUri);
      console.log(`[SipPhone] Transferred to ${sanitizedDest}`);
      return true;
    } catch (error) {
      console.error('[SipPhone] Transfer error:', error);
      return false;
    }
  }

  public sendDTMF(tone: string): boolean {
    if (!this.currentSession || this.currentSession.state !== SessionState.Established) {
      return false;
    }

    try {
      const sessionDescriptionHandler = this.currentSession.sessionDescriptionHandler as SessionDescriptionHandler;
      const peerConnection = sessionDescriptionHandler?.peerConnection;
      
      if (peerConnection) {
        const senders = peerConnection.getSenders();
        const audioSender = senders.find(s => s.track?.kind === 'audio');
        if (audioSender && 'dtmf' in audioSender && audioSender.dtmf) {
          (audioSender.dtmf).insertDTMF(tone, 100, 70);
          console.log(`[SipPhone] Sent DTMF: ${tone}`);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('[SipPhone] DTMF error:', error);
      return false;
    }
  }

  public isConnected(): boolean {
    return this.registerer?.state === RegistererState.Registered;
  }

  public isInCall(): boolean {
    return this.currentSession?.state === SessionState.Established;
  }

  public getCurrentCallInfo(): CallInfo | undefined {
    return this.getCallInfo();
  }
}

export const sipPhoneService = new SipPhoneService();
